import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Upload } from "lucide-react";
import { BACKEND_URL, C } from "../../lib/api";
import { RotationPicker } from "../RotationPicker";

const SLOTS = [
  ["tv", "Table-room TVs", "Shown full-screen on the TVs in your table room, in turn with your logo. Make them 16:9 — 1600 × 900 PNG is ideal."],
  ["posters", "Lobby — What's on", "The big promo screen in your club lobby. Portrait or square posters work best."],
  ["photos", "Lobby — The room", "Photos of your venue and games, shown on the small lobby screen."],
];

const Slot = ({ slot, title, hint, items, onUpload, onRemove, busy }) => {
  const input = useRef(null);
  return (
    <div className="club-card p-5 flex flex-col gap-3" data-testid={`club-screens-${slot}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-[0.22em] club-accent-text">{title}</span>
          <span className="text-[11px] club-soft max-w-xl">{hint}</span>
        </div>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`club-screens-file-${slot}`}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(slot, f); }} />
        <button data-testid={`club-screens-upload-${slot}`} disabled={busy} onClick={() => input.current?.click()}
          className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em] flex items-center gap-2 disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload a picture"}
        </button>
      </div>
      <div className="flex gap-3 flex-wrap">
        {items.map((p) => (
          <div key={p.media_id} className="relative group" data-testid={`club-screens-item-${p.media_id}`}>
            <img src={`${BACKEND_URL}${p.url}`} alt={p.label} className={`${slot === "tv" ? "w-48 h-[108px]" : "w-28 h-36"} object-cover rounded border`} style={{ borderColor: "var(--c-line)" }} />
            <button data-testid={`club-screens-remove-${p.media_id}`} onClick={() => onRemove(p.media_id)} title="Remove"
              className="absolute top-1 right-1 club-chip p-1.5 opacity-80 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        {!items.length && <span className="text-xs club-soft flex items-center gap-2"><ImagePlus className="w-4 h-4" />Nothing uploaded — this screen shows {slot === "tv" ? "your logo" : "the pictures we set up for you"}.</span>}
      </div>
    </div>
  );
};

export const ScreensTab = ({ api }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api().get("/api/affiliate/screens").then((r) => setS(r.data)).catch(() => setMsg("Could not load your screens.")), [api]);
  useEffect(() => { load(); }, [load]);

  const upload = async (slot, file) => {
    setBusy(slot); setMsg("");
    const fd = new FormData(); fd.append("slot", slot); fd.append("label", file.name.replace(/\.[^.]+$/, "")); fd.append("file", file);
    try { const r = await api().post("/api/affiliate/screens/upload", fd); setS(r.data); setMsg("Uploaded — open tables pick it up within a minute."); }
    catch (e) { setMsg(e?.response?.data?.detail?.detail || "That upload did not go through — try again."); }
    finally { setBusy(""); }
  };
  const remove = async (id) => { const r = await api().delete(`/api/affiliate/screens/${id}`); setS(r.data); };
  const setting = async (patch) => { const r = await api().patch("/api/affiliate/screens/settings", { ...s.settings, ...patch }); setS(r.data); };

  if (!s) return <p className="text-xs club-soft" data-testid="club-screens-loading">{msg || "Loading your screens…"}</p>;
  const st = s.settings;
  return (
    <section className="flex flex-col gap-5" data-testid="club-admin-screens">
      <div className="club-card p-5 flex gap-6 flex-wrap items-center text-sm" data-testid="club-screens-settings">
        <label className="flex items-center gap-2"><input type="checkbox" checked={st.tv_on} onChange={(e) => setting({ tv_on: e.target.checked })} data-testid="club-screens-tv-on" /> Rotate my pictures on the table-room TVs</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={st.tv_logo} onChange={(e) => setting({ tv_logo: e.target.checked })} data-testid="club-screens-tv-logo" /> Keep my logo in the rotation</label>
        <label className="flex items-center gap-2">Change every
          <select value={st.seconds} onChange={(e) => setting({ seconds: Number(e.target.value) })} data-testid="club-screens-seconds" className="club-input px-2 py-1 text-sm">
            {[6, 8, 10, 15, 20, 30].map((n) => <option key={n} value={n}>{n}s</option>)}
          </select>
        </label>
        {msg && <span className="text-xs club-soft" data-testid="club-screens-msg">{msg}</span>}
      </div>
      {SLOTS.map(([k, title, hint]) => (
        <Slot key={k} slot={k} title={title} hint={hint} items={s[k] || []} onUpload={upload} onRemove={remove} busy={busy === k} />
      ))}
    </section>
  );
};

const day = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

export const MembersTab = ({ api }) => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api().get("/api/affiliate/members").then((r) => setD(r.data)).catch(() => setD({ members: [], error: true })); }, [api]);
  if (!d) return <p className="text-xs club-soft">Loading your members…</p>;
  const rows = d.members.filter((m) => !q || `${m.name} ${m.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="flex flex-col gap-4" data-testid="club-admin-members">
      <div className="flex items-center gap-4 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="club-members-search" className="club-input px-3 py-2 text-sm w-72" />
        <span className="text-xs club-soft" data-testid="club-members-count">{d.members.length} members · {d.tournaments_run} of your tournaments played</span>
      </div>
      <div className="club-card overflow-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] club-accent-text">
            <tr>{["Player", "Joined", "Tournaments", "Wins", "Cashes", "Best", "Chips won", "Hands", "Hands won", "Last played"].map((h, i) => <th key={h} className={`p-3 ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.user_id} data-testid={`club-member-${m.user_id}`} className="border-t" style={{ borderColor: "var(--c-line)" }}>
                <td className="p-3"><div>{m.name}{m.on_code && <span className="ml-2 club-badge px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em]">your code</span>}</div><div className="text-[11px] club-soft">{m.email}</div></td>
                <td className="p-3 club-soft">{day(m.joined)}</td>
                <td className="p-3 text-right">{m.played}</td>
                <td className="p-3 text-right club-accent-text">{m.wins}</td>
                <td className="p-3 text-right">{m.cashes}</td>
                <td className="p-3 text-right">{m.best ?? "—"}</td>
                <td className="p-3 text-right font-mono">{m.won_chips.toLocaleString()}</td>
                <td className="p-3 text-right">{m.hands.toLocaleString()}</td>
                <td className="p-3 text-right">{m.hands_won.toLocaleString()}</td>
                <td className="p-3 club-soft">{day(m.last)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="p-4 text-center club-soft text-xs">{d.error ? "Could not load your members — try again shortly." : "No members yet — players appear here when they sign up on your code or play one of your tournaments."}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const GAME_BLANK = {
  name: "", game: "HOLDEM", rotation: [], max_seats: 8, free: true, buy_in: 0, prize_pool_fixed: 100000, prize_per_entrant: 2000,
  starting_stack: 50000, level_minutes: 10, late_reg_levels: 5, re_entries: 0, mode: "FREEZEOUT", rebuy_levels: 0,
  rebuy_cost: 0, rebuy_chips: 0, addon: false, addon_at_level: 6, addon_cost: 0, addon_chips: 0, break_every_levels: 4,
  break_minutes: 5, min_entrants: 2, max_entrants: 100, bots: false, bot_count: 0, run_date: "", times: "19:30", club_prize: "", status: "open",
};
const NUMS = [["starting_stack", "Starting stack"], ["level_minutes", "Level minutes"], ["late_reg_levels", "Late rego (levels)"],
  ["break_every_levels", "Break every (levels, 0 = none)"], ["break_minutes", "Break minutes"], ["min_entrants", "Min players"],
  ["max_entrants", "Max players (≤100)"], ["re_entries", "Re-entries"]];
const REBUY = [["rebuy_levels", "Re-buy for (levels)"], ["rebuy_cost", "Re-buy cost"], ["rebuy_chips", "Re-buy chips"]];
const ADDON = [["addon_at_level", "Add-on at level"], ["addon_cost", "Add-on cost"], ["addon_chips", "Add-on chips"]];

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.16em] club-soft">{label}{children}</label>
);

const GameForm = ({ init, tier, onSave, onCancel, busy }) => {
  const [f, setF] = useState(init);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const num = ([k, l]) => (
    <Field key={k} label={l}><input type="number" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`club-game-form-${k}`} className="club-input px-2 py-1.5 text-sm w-full" /></Field>
  );
  const core = tier === "TIER1";
  return (
    <div className="club-card p-5 flex flex-col gap-4" data-testid="club-game-form">
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Tournament name"><input value={f.name} maxLength={48} onChange={(e) => set("name", e.target.value)} data-testid="club-game-form-name" className="club-input px-2 py-1.5 text-sm" /></Field>
        <Field label="Game"><select value={f.game} disabled={core} onChange={(e) => set("game", e.target.value)} data-testid="club-game-form-game" className="club-input px-2 py-1.5 text-sm disabled:opacity-60">
          {(core ? [["HOLDEM", "Texas Hold'em"]] : [...Object.entries(C.GAME_RULES).map(([k, r]) => [k, r.label]), ["ROTATION", "ROTATION — mixed games"]]).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        {f.game === "ROTATION" && <RotationPicker className="md:col-span-4" value={f.rotation || []} onChange={(rotation) => set("rotation", rotation)} />}
        <Field label="Seats per table"><select value={f.max_seats} onChange={(e) => set("max_seats", Number(e.target.value))} data-testid="club-game-form-seats" className="club-input px-2 py-1.5 text-sm">
          {[2, 6, 7, 8, 9].filter((n) => !core || n <= 8).map((n) => <option key={n} value={n}>{n}-max</option>)}</select></Field>
        <Field label="Date (blank = every day)"><input type="date" value={f.run_date} onChange={(e) => set("run_date", e.target.value)} data-testid="club-game-form-date" className="club-input px-2 py-1.5 text-sm" /></Field>
        <Field label="Start time(s), AEST"><input value={f.times} onChange={(e) => set("times", e.target.value)} placeholder="7:30pm" data-testid="club-game-form-times" className="club-input px-2 py-1.5 text-sm" /></Field>
        <Field label="Entry"><select value={f.free ? "free" : "buyin"} onChange={(e) => set("free", e.target.value === "free")} data-testid="club-game-form-free" className="club-input px-2 py-1.5 text-sm">
          <option value="free">Freeroll</option><option value="buyin">Chip buy-in</option></select></Field>
        {f.free ? (<>
          <Field label="Guaranteed pool (chips)"><input type="number" value={f.prize_pool_fixed} onChange={(e) => set("prize_pool_fixed", e.target.value)} data-testid="club-game-form-pool" className="club-input px-2 py-1.5 text-sm" /></Field>
          <Field label="+ per player (chips)"><input type="number" value={f.prize_per_entrant} onChange={(e) => set("prize_per_entrant", e.target.value)} data-testid="club-game-form-per" className="club-input px-2 py-1.5 text-sm" /></Field>
        </>) : num(["buy_in", "Buy-in (chips)"])}
        {NUMS.map(num)}
        <Field label="Format"><select value={f.mode} onChange={(e) => set("mode", e.target.value)} data-testid="club-game-form-mode" className="club-input px-2 py-1.5 text-sm">
          <option value="FREEZEOUT">Freezeout</option><option value="REBUY">Re-buy</option></select></Field>
        {f.mode === "REBUY" && REBUY.map(num)}
        <Field label="Add-on"><select value={f.addon ? "y" : "n"} onChange={(e) => set("addon", e.target.value === "y")} data-testid="club-game-form-addon" className="club-input px-2 py-1.5 text-sm">
          <option value="n">No add-on</option><option value="y">Add-on</option></select></Field>
        {f.addon && ADDON.map(num)}
        <Field label="Practice bots"><select value={f.bots ? "y" : "n"} onChange={(e) => set("bots", e.target.value === "y")} data-testid="club-game-form-bots" className="club-input px-2 py-1.5 text-sm">
          <option value="n">No bots</option><option value="y">Fill with bots</option></select></Field>
        {f.bots && num(["bot_count", "How many bots"])}
        <Field label="Club prize (e.g. a seat in Sunday's main)"><input value={f.club_prize} maxLength={90} onChange={(e) => set("club_prize", e.target.value)} data-testid="club-game-form-prize" className="club-input px-2 py-1.5 text-sm" /></Field>
      </div>
      <div className="flex gap-3 items-center">
        <button disabled={busy || f.name.trim().length < 2} onClick={() => onSave(f)} data-testid="club-game-form-save" className="club-btn px-6 py-2.5 text-xs uppercase tracking-[0.2em] disabled:opacity-50">{busy ? "Saving…" : init._edit ? "Save changes" : "Create tournament"}</button>
        <button onClick={onCancel} data-testid="club-game-form-cancel" className="club-chip px-5 py-2.5 text-xs uppercase tracking-[0.2em]">Cancel</button>
        <span className="text-[11px] club-soft">Blinds follow the Players Room tournament structure. Players see it in your room as soon as you save.</span>
      </div>
    </div>
  );
};

const INTS = ["max_seats", "buy_in", "prize_pool_fixed", "prize_per_entrant", "starting_stack", "level_minutes", "late_reg_levels", "re_entries",
  "rebuy_levels", "rebuy_cost", "rebuy_chips", "addon_at_level", "addon_cost", "addon_chips", "break_every_levels", "break_minutes",
  "min_entrants", "max_entrants", "bot_count"];

export const GamesTab = ({ api }) => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api().get("/api/affiliate/games").then((r) => setD(r.data)).catch(() => setMsg("Could not load your tournaments.")), [api]);
  useEffect(() => { load(); }, [load]);
  const err = (e) => setMsg(e?.response?.data?.detail?.detail || (Array.isArray(e?.response?.data?.detail) ? "Check the numbers — one is out of range." : "That did not save — try again."));

  const save = async (f) => {
    setBusy(true); setMsg("");
    const body = { ...f, times: String(f.times).split(/[,\s]+/).filter(Boolean) };
    INTS.forEach((k) => { body[k] = Number(body[k]) || 0; });
    body.min_entrants = Math.max(2, body.min_entrants); body.max_entrants = Math.min(100, Math.max(2, body.max_entrants || 100));
    delete body._edit;
    try {
      const r = f._edit ? await api().patch(`/api/affiliate/games/${f._edit}`, body) : await api().post("/api/affiliate/games", body);
      setD(r.data); setForm(null); setMsg(f._edit ? "Saved." : "Created — it's in your room now.");
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const remove = async (ev) => {
    if (!window.confirm(`Remove ${ev.name}? Anyone registered is refunded.`)) return;
    try { const r = await api().delete(`/api/affiliate/games/${ev.event_id}`); setD(r.data); } catch (e) { err(e); }
  };
  const toggle = async (ev) => {
    const { live, times, event_id, created_at, created_by, affiliate_id, club_slug, affiliate_event, affiliate_approved, schedule, vip_top3, fee_percent, ...rest } = ev;
    try { const r = await api().patch(`/api/affiliate/games/${event_id}`, { ...rest, times, status: ev.status === "open" ? "closed" : "open" }); setD(r.data); } catch (e) { err(e); }
  };

  if (!d) return <p className="text-xs club-soft">{msg || "Loading your tournaments…"}</p>;
  return (
    <section className="flex flex-col gap-4" data-testid="club-admin-games">
      <div className="flex items-center gap-4 flex-wrap">
        {!form && <button data-testid="club-game-new" onClick={() => setForm({ ...GAME_BLANK })} className="club-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]">New tournament</button>}
        {msg && <span className="text-xs club-soft" data-testid="club-games-msg">{msg}</span>}
      </div>
      {form && <GameForm key={form._edit || "new"} init={form} tier={d.tier} busy={busy} onSave={save} onCancel={() => setForm(null)} />}
      {d.games.map((ev) => (
        <div key={ev.event_id} className="club-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`club-game-${ev.event_id}`}>
          <div className="flex flex-col">
            <span className="club-display uppercase">{ev.name}</span>
            <span className="text-xs club-soft">
              {ev.run_date || "Every day"} · {(ev.times || []).join(", ")} AEST · {ev.max_seats}-max · {ev.free ? `Freeroll ${Number(ev.prize_pool_fixed).toLocaleString()} GTD` : `Buy-in ${Number(ev.buy_in).toLocaleString()}`} · {Number(ev.starting_stack).toLocaleString()} stack · {ev.level_minutes} min levels
            </span>
            {ev.live && <span className="text-[11px] club-accent-text" data-testid={`club-game-live-${ev.event_id}`}>{ev.live.status} · {ev.live.registered} registered</span>}
          </div>
          <div className="flex gap-2">
            <button data-testid={`club-game-toggle-${ev.event_id}`} onClick={() => toggle(ev)} className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em]">{ev.status === "open" ? "Hide" : "Show"}</button>
            <button data-testid={`club-game-edit-${ev.event_id}`} onClick={() => setForm({ ...GAME_BLANK, ...ev, times: (ev.times || []).join(", "), _edit: ev.event_id })} className="club-chip px-4 py-2 text-[11px] uppercase tracking-[0.18em]">Edit</button>
            <button data-testid={`club-game-delete-${ev.event_id}`} onClick={() => remove(ev)} className="club-chip p-2"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
      {!d.games.length && !form && <p className="text-xs club-soft">No tournaments built yet — press New tournament to set one up.</p>}
    </section>
  );
};
